import { useState, useEffect, useCallback } from "react";
import * as cmService from "../services/careManagerDataService";

/**
 * Hook para cargar datos del Care Manager Dashboard por location.
 * Carga los 6 datasets en paralelo con loading individual por panel.
 *
 * @param {string|null} locationKey - Key de la location seleccionada
 * @returns {{ overview, transitions, unmanagedRisk, erUtilization, outreach, polypharmacy, loading, error }}
 */
export default function useCareManagerData(locationKey) {
  const [overview, setOverview] = useState(null);
  const [transitions, setTransitions] = useState(null);
  const [unmanagedRisk, setUnmanagedRisk] = useState(null);
  const [erUtilization, setERUtilization] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [polypharmacy, setPolypharmacy] = useState(null);

  const [loading, setLoading] = useState({
    overview: true,
    transitions: true,
    unmanagedRisk: true,
    erUtilization: true,
    outreach: true,
    polypharmacy: true,
  });

  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!locationKey) return;

    // Reset loading states
    setLoading({
      overview: true,
      transitions: true,
      unmanagedRisk: true,
      erUtilization: true,
      outreach: true,
      polypharmacy: true,
    });
    setError(null);

    const results = await Promise.allSettled([
      cmService.getOverview(locationKey),
      cmService.getTransitions(locationKey),
      cmService.getUnmanagedRisk(locationKey),
      cmService.getERUtilization(locationKey),
      cmService.getOutreach(locationKey),
      cmService.getPolypharmacy(locationKey),
    ]);

    const [overviewR, transitionsR, unmanagedR, erR, outreachR, polyR] = results;

    if (overviewR.status === "fulfilled") {
      setOverview(overviewR.value);
    } else {
      console.error("Error loading overview:", overviewR.reason);
      setError(overviewR.reason);
    }
    setLoading((prev) => ({ ...prev, overview: false }));

    if (transitionsR.status === "fulfilled") {
      setTransitions(transitionsR.value);
    } else {
      console.error("Error loading transitions:", transitionsR.reason);
    }
    setLoading((prev) => ({ ...prev, transitions: false }));

    if (unmanagedR.status === "fulfilled") {
      setUnmanagedRisk(unmanagedR.value);
    } else {
      console.error("Error loading unmanaged risk:", unmanagedR.reason);
    }
    setLoading((prev) => ({ ...prev, unmanagedRisk: false }));

    if (erR.status === "fulfilled") {
      setERUtilization(erR.value);
    } else {
      console.error("Error loading ER utilization:", erR.reason);
    }
    setLoading((prev) => ({ ...prev, erUtilization: false }));

    if (outreachR.status === "fulfilled") {
      setOutreach(outreachR.value);
    } else {
      console.error("Error loading outreach:", outreachR.reason);
    }
    setLoading((prev) => ({ ...prev, outreach: false }));

    if (polyR.status === "fulfilled") {
      setPolypharmacy(polyR.value);
    } else {
      console.error("Error loading polypharmacy:", polyR.reason);
    }
    setLoading((prev) => ({ ...prev, polypharmacy: false }));
  }, [locationKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    overview,
    transitions,
    unmanagedRisk,
    erUtilization,
    outreach,
    polypharmacy,
    loading,
    error,
  };
}
